import { ViewCustomer } from '@xeetr/types/models'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { Button } from '../ui/button'
import { DialogFooter } from '../ui/dialog'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'

interface CustomerFormProps {
  customer?: ViewCustomer
  onSubmit: (customer: ViewCustomer) => void
  onCancel: () => void
}

interface CustomerFormData {
  firstName: string
  lastName: string
  companyName: string
  phone: string
  mail: string
  note: string
  street: string
  postalCode: string
  city: string
  customFields: Record<string, string>
}

const CustomerForm = ({ customer, onSubmit, onCancel }: CustomerFormProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<CustomerFormData>({
    defaultValues: {
      firstName: customer?.firstName || '',
      lastName: customer?.lastName || '',
      companyName: customer?.companyName || '',
      phone: customer?.phone || '',
      mail: customer?.mail || '',
      note: customer?.info?.note || '',
      street: customer?.info?.address?.street || '',
      postalCode: customer?.info?.address?.postalCode || '',
      city: customer?.info?.address?.city || '',
    },
  })

  useEffect(() => {
    if (customer) {
      reset({
        firstName: customer.firstName,
        lastName: customer.lastName,
        companyName: customer.companyName || '',
        phone: customer.phone,
        mail: customer.mail,
        note: customer.info?.note || '',
        street: customer.info?.address?.street || '',
        postalCode: customer.info?.address?.postalCode || '',
        city: customer.info?.address?.city || '',
      })
    }
  }, [customer, reset])

  const onFormSubmit = (data: CustomerFormData) => {
    const customerData: ViewCustomer = {
      id: customer?.id,
      tenantId: customer?.tenantId || '', // This will be set by the API for new customers
      firstName: data.firstName,
      lastName: data.lastName,
      companyName: data.companyName || undefined,
      phone: data.phone,
      mail: data.mail,
      count: customer?.count || 0,
      updated: customer?.updated,
      info: {
        note: data.note || '',
        address: {
          street: data.street || undefined,
          postalCode: data.postalCode || undefined,
          city: data.city || undefined,
        },
      },
    }
    onSubmit(customerData)
  }

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">Vorname *</Label>
          <Input
            id="firstName"
            {...register('firstName', { required: 'Vorname ist erforderlich' })}
            placeholder="Vorname eingeben"
          />
          {errors.firstName && (
            <p className="text-destructive text-sm">{errors.firstName.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="lastName">Nachname *</Label>
          <Input
            id="lastName"
            {...register('lastName', { required: 'Nachname ist erforderlich' })}
            placeholder="Nachname eingeben"
          />
          {errors.lastName && <p className="text-destructive text-sm">{errors.lastName.message}</p>}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="companyName">Firmenname</Label>
        <Input
          id="companyName"
          {...register('companyName')}
          placeholder="Firmenname eingeben (optional)"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone">Telefon</Label>
        <Input id="phone" type="tel" {...register('phone')} placeholder="Telefonnummer eingeben" />
        {errors.phone && <p className="text-destructive text-sm">{errors.phone.message}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="mail">E-Mail</Label>
        <Input
          id="mail"
          type="email"
          {...register('mail', {
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
            },
          })}
          placeholder="E-Mail-Adresse eingeben"
        />
        {errors.mail && <p className="text-destructive text-sm">{errors.mail.message}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="note">Notiz</Label>
        <Textarea
          id="note"
          {...register('note')}
          placeholder="Notiz eingeben (optional)"
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="street">Straße</Label>
        <Input id="street" {...register('street')} placeholder="Straße eingeben (optional)" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="postalCode">PLZ</Label>
          <Input id="postalCode" {...register('postalCode')} placeholder="PLZ eingeben" />
        </div>

        <div className="space-y-2">
          <Label htmlFor="city">Stadt</Label>
          <Input id="city" {...register('city')} placeholder="Stadt eingeben" />
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Abbrechen
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Speichern...' : customer ? 'Kunde aktualisieren' : 'Kunden erstellen'}
        </Button>
      </DialogFooter>
    </form>
  )
}

export default CustomerForm
